// this is a component which handles both the Lexa Speech and the voice recognition of the app
// no other component should directly control either of these two things
// transcipts can be parsed elsewhere, but listening should only be turned on and off from here
// in addition this is the only component which should start or end speech
// any request from another component to start listening, stop listening, speak,
// or stop speaking, should go through App's instance of this component


import React from 'react';
import LexAudioRecorder from '../../audiocapture/recorder';
import SpeechRecognition from './SpeechRecognition/SpeechRecognition';
import LexaSpeech from './LexaSpeech';
import {saveSessionAudio, startPlacementSession, endPlacementSession, pauseSpeechSession, resumeSpeechSession, resetPlacementTranscript, initSocketCounters, getDisconnects, placementProxy, initUniversalSocket, startRecordingSession, buildAdhocAudio} from '../../../services/SpeechStream';
import { getUserData } from "../../../services/API";
import {supportsSpeechRec} from "../../../services/environment";
import {logSessionInfo} from '../../../services/logger';
import { getUserLocale } from '../../../services/environment';
import * as Sentry from '@sentry/browser';
import device from '../../../services/device/device';

// redux dependencies
import { connect } from 'react-redux';
import speechCreators from './state/creators';
import { logSentryException, shouldForceHTML5Audio, iOSSupportsAudioSession } from '../../../services/util';

var stringSimilarity = require('string-similarity');

const myRecorder = new LexAudioRecorder({preset: 'speech_recognition'});

let recordingSession = {};
let buildAudio = false;
let endMetadata = null;
let onSpeechStart = null;
let silentSegmentRun = 0;

const CONSECUTIVE_SILENT_THRESHOLD = 20;

let overallSilentSegments = 0;
let nullSegments = 0;
let totalSegments = 0;
let mutedRetries = 0;
let disableBackend = false;
let usePhonASR = false;
let phonemePlacementEnabled = false;

let speechQueue = [];
let speechPending = false;
let abortSpeechPending = null;
let dancePending = false;
let struggleMonitorEnabled = false;
let abortDancePending = false;
let isMounted = false;
let loggedOut = false;

// this is used for phoneme placement when we have phrases which are a mix of phonemes and blended words.
// we combine the two transcripts here: standard placement for the blended word and phoneme asr for the phonemes
let pastCombinedPhonemeTranscript = "";

let sound = null;
let audioDestination = null;

class Speech extends React.Component {
  constructor (props){
    super(props);

    this.locale = getUserLocale();

    this.state = {
      isListening: false,
      isSpeaking: false,
      isQuiet: true,
      abortSpeech: [],
      abortDance: () => {},
      matchRequest: null,
      transcript: {
        interim: null,
        final: null,
      },
      lexaSpeechProps: {}, //TODO: this is a temporary solution, eventually lexa speech will be a function we can invoke without updating props
      avatarSpeechProps: {
        textToSpeak: null,
      },
      pendingStartListening: false,
      pendingStartListeningSkipCallback: false,
      functions: {
        setAbortSpeech: this.setAbortSpeech.bind(this),
        setAbortDance: this.setAbortDance.bind(this),
      },
      buildAudio: false,
      blankRecording: false,
      altPlacement: false,
      forceKaldi: false,
      placementConnected: false,
      speechStreamFailure: false,
      onSpeechStreamFailure: null,
      inBackground: false,
    };

    this.setAvatarSpeech = this.setAvatarSpeech.bind(this);
    this.speakWithPromise = this.speakWithPromise.bind(this);
    this.playAppSounds = this.playAppSounds.bind(this);
    this.playAppSoundsWithPromise = this.playAppSoundsWithPromise.bind(this);
    this.onFinishAppSounds = this.onFinishAppSounds.bind(this);
  }

  componentDidMount(){
    isMounted = true;
    console.log("speech mounted");
    // export some functions and info via the provided function
    //TODO: consider using redux here
    this.initPlacement();

    myRecorder.ondataavailable = {handler: this.onDataReady.bind(this)};
    myRecorder.onstop = {handler: this.onRecorderStop.bind(this)};
    myRecorder.onerror = {handler: this.onRecorderError.bind(this)};
    myRecorder.onquiet = {handler: this.onQuiet.bind(this)};
    myRecorder.onunquiet = {handler: this.onUnquiet.bind(this)};
    myRecorder.onmute = {handler: this.onMute.bind(this)};
    myRecorder.onunmute = {handler: this.onUnmute.bind(this)};
    buildAudio = false;
    silentSegmentRun = 0; //Initialize this here and only here. for a given instance of the speech component, we want
                          // to trigger silence detection across runs.
    this.setState({altPlacement: this.useAltPlacement()});
  }

  componentWillUnmount(){
    console.log("unmounting speech");
    isMounted = false;
    //Get rid of all the handlers we constructed
    myRecorder.ondataavailable = {remove: true};
    myRecorder.onstop = {remove: true};
    myRecorder.onerror = {remove: true};
    myRecorder.onquiet = {remove: true};
    myRecorder.onunquiet = {remove: true};
    myRecorder.onmute = {remove: true};
    myRecorder.onunmute = {remove: true};
    myRecorder.stop();
    myRecorder.closeAudioContext();

    loggedOut = true;

    if(sound){
      this.destroySound();
    }
  }

  componentDidUpdate(prevProps,prevState){
    // if we have new transcription info, pass it along
    if((this.props.transcript && this.props.transcript !== prevProps.transcript)
        || (!this.state.altPlacement && (this.props.finalTranscript && this.props.finalTranscript !== prevProps.finalTranscript))
        || (this.state.altPlacement && (this.state.finalTranscript && this.state.finalTranscript !== prevState.finalTranscript))
        || (usePhonASR && this.state.phonASRPhonemes && prevState.phonASRPhonemes != this.state.phonASRPhonemes)){ // if we're using the phonASR, then this logic should trigger when that updates rather than when the transcript updates
      if(this.state.transcriptRequest){
        let transcriptToSend = this.state.altPlacement ? this.state.finalTranscript : this.props.interimTranscript;
        if(usePhonASR) transcriptToSend = this.state.phonASRPhonemes;
        this.state.transcriptRequest.callback(transcriptToSend);
      }
      //If the speech component has an active match request, don't propagate props to
      // anyone else listening. This was put in to fix a problem in the reader
      // during the speech selection menu.
      // TODO: Really think about this. It would be nice to listen for phrases like "Lexa, help" on top of the student's reading.
      if(this.state.matchRequest){
        if((this.props.finalTranscript)
            || ((this.state.matchRequest.checkForMatch === false) && this.props.transcript)){
          this.checkMatch();
        }
      }else{
        let finalState = this.state.altPlacement ? this.state.finalTranscript : this.props.finalTranscript;
        let interimState = this.state.altPlacement ? this.state.interimTranscript : this.props.transcript;

        // if phoneme placement is enabled, blend the phoneme asr and placement transcripts together
        if(this.getPhonemePlacementEnabled()){
          const blendedTranscripts = this.blendTranscriptWithPhonemes(finalState, interimState);
          finalState = blendedTranscripts.final;
          interimState = blendedTranscripts.interim;
        }

        this.setState({
          transcript: {
            final: finalState,
            interim: interimState,
          },
        });
      }
    }
    if(this.props.forceKaldi !== prevProps.forceKaldi){
      this.setState({forceKaldi: this.props.forceKaldi},this.initPlacement);
    }
  }

  // PHONEME PLACEMENT LOGIC -- this will only be used if the story has a tag that has phoneme placement enabled
  //   if phoneme placement is enabled, blend the phoneme asr and placement transcripts together
  blendTranscriptWithPhonemes(finalTranscript, interimTranscript){
    let final = finalTranscript;
    let interim = interimTranscript;
    if(this.getPhonemePlacementEnabled()){ //belts and suspenders to make sure we don't do this unless we really want to

      // if we're currently in phoneme mode (because the current word is a phoneme),
      //  overwrite the transcript with the results from the phoneme ASR
      if(this.getPhonemeMode() && (this.state.phonASRPhonemes || this.state.phonASRPhonemes == '')) {
        final = this.state.phonASRPhonemes;
        interim = this.state.phonASRPhonemes;
      }

      // if we have accumulated a transcript from past phases of listening for phonemes or blended
      //  words in pastCombinedPhonemeTranscript, then prepend that value to our current transcript
      if(pastCombinedPhonemeTranscript && pastCombinedPhonemeTranscript != ""){
        final = pastCombinedPhonemeTranscript + " " + final;
        interim = pastCombinedPhonemeTranscript + " " + interim;
      }
    }
    return {final, interim};
  }

  initPlacement(){
    let useAltPlacement = this.useAltPlacement();

    //Inject alternate methods depending on the placement manager configuration
    const setGrammarFunction = useAltPlacement ? this.speechFunctionStub.bind(this) : this.props.setGrammar;
    const setOnSpeechStartFunction = useAltPlacement ? this.setOnSpeechStart.bind(this) : this.props.setOnSpeechStart;
    const setLanguageFunction = useAltPlacement ? this.speechFunctionStub.bind(this) : this.props.setLanguage;

    this.setState({
      altPlacement: useAltPlacement,
      numPhonemes: 0,
      abPhonemes: '',
      functions: {
        speak: this.queueSpeech.bind(this),
        speakWithPromise: this.speakWithPromise.bind(this),
        speakSequence: this.speakSequence.bind(this),
        speakAudioFile: this.speakAudioFile.bind(this),
        startListening: this.startListening.bind(this),
        stopListening: this.stopListening.bind(this),
        resetTranscript: this.resetTranscriptFunctionToExport.bind(this),
        setOnSpeechStart: setOnSpeechStartFunction,
        setGrammar: setGrammarFunction,
        setLanguage: setLanguageFunction,
        abortSpeech: this.abortSpeech.bind(this),
        abortDance: this.abortDance.bind(this),
        setAbortDance: this.setAbortDance.bind(this),
        initializeSpeechSession: this.initializeSpeechSession.bind(this),
        getPlacementConfig: this.getPlacementConfig.bind(this),
        requestTranscript: this.requestTranscript.bind(this),
        cancelTranscriptRequest: this.cancelTranscriptRequest.bind(this),
        requestMatch: this.requestMatch.bind(this),
        cancelMatchRequest: this.cancelMatchRequest.bind(this),
        enableRecording: this.enableRecording.bind(this),
        initializeRecorderIfNeeded: this.initializeRecorderIfNeeded.bind(this),
        disableRecording: this.disableRecording.bind(this),
        setAbortSpeech: this.setAbortSpeech.bind(this),
        isRecording: this.isRecording.bind(this),
        silentSegments: this.silentSegments.bind(this),
        volumeStatistics: this.volumeStatistics.bind(this),
        getSpeechState: this.getSpeechState.bind(this),
        playDance: this.playDance.bind(this),
        onDanceStart: this.onDanceStart.bind(this),
        startVolumeProcessing: this.startVolumeProcessing.bind(this),
        stopVolumeProcessing: this.stopVolumeProcessing.bind(this),
        playAppSounds: this.playAppSounds.bind(this),
        playAppSoundsWithPromise: this.playAppSoundsWithPromise.bind(this),
        togglePhonemeListeningOn: this.togglePhonemeListeningOn.bind(this),
        togglePhonemeListeningOff: this.togglePhonemeListeningOff.bind(this),
        setPhonemePlacementEnabled: this.setPhonemePlacementEnabled.bind(this),
        getPhonemePlacementEnabled: this.getPhonemePlacementEnabled.bind(this),
        setAudioDestination: this.setAudioDestination.bind(this),
        clearAudioDestination: this.clearAudioDestination.bind(this),
      },
    });
  }

  // Returns true if we should be using the Kaldi placement manager
  useAltPlacement(){
    //Assume that we're using Kaldi. This is the safest bet since if browser detect
    // fails the worst that will happen is that we'll fall back on chome, which is safe
    let useAltPlacement = true;
    let useStruggleMonitor = getUserData().useStruggleMonitor && getUserData().appSettings.useStruggleMonitor;

    //Switch between using Google's placement manager and Kaldi placement between the ORF
    // and the dyslexia test.
    if (device.browser) {
      if(supportsSpeechRec(device.browser) && ((!this.props.forceKaldi && !getUserData().tutorWhitelist && !useStruggleMonitor) || (this.props.forceGooglePM))){
        useAltPlacement = false;
      }
    }

    console.log("Kaldi:",useAltPlacement)
    return(useAltPlacement);
  }

  //This is used to stub out functions that aren't valid for the alt placement
  speechFunctionStub(){
  }

  setAvatarSpeech(avatarSpeechProps){
    this.setState({avatarSpeechProps});
  }

  onQuiet(evt){
    this.setState({
      isQuiet: true,
    });
  }

  onUnquiet(evt){
    this.setState({
      isQuiet: false,
    });
  }

  onMute(evt){
    console.log("MUTED",evt);
  }

  onUnmute(evt){
    console.log("UNMUTED",evt);
  }

  //************** Recording Methods */
  initializeRecorderIfNeeded(handleError, handleSuccess) {
    if (!loggedOut && !this.recorderInitialized) {
      myRecorder.init({preset: 'speech_recognition'})
        .then(()=>{
          this.recorderInitialized = true;
          handleSuccess()
        }).catch(error => {
        handleError(error)
      })
    }else{
      handleSuccess()
    }
  }

  // These two methods allow a component to control recording with an identifier that will be attached to any
  // and all audio fragments. Maybe startRecording
  enableRecording(recordingProps){
    recordingProps.clearHistory = true;
    recordingSession = recordingProps;
    disableBackend = recordingSession.disableBackend ? true : false;

    startRecordingSession();
    if((this.state.isListening) && (this.state.activityId)){
      myRecorder.start(recordingProps);
    }
  }

  disableRecording(inBuildAudio=false,inEndingMetadata=null){
    endMetadata = inEndingMetadata;
    buildAudio = inBuildAudio;
    //Handle the case where we're not recording but someone
    // wants to send the final packet to the backend
    if(!myRecorder.isRecording && inBuildAudio){
      this.onDataReady({
        detail: {
          props: recordingSession,
          data: new Blob([0],{type: 'audio/wav'}),
        },
      });
    }
    if(!inBuildAudio){
      recordingSession = {};
      disableBackend = false;
      myRecorder.stop();
    }
  }

  isRecording(){
    return myRecorder.isRecording;
  }

  setOnSpeechStart(callback){
    onSpeechStart = callback;
  }


  onDataReady(evt) {
    totalSegments++;
    //If the buffer contains all zeros and we're still actively sending, check for muted audio
    if((evt.detail.allZeros || !evt.detail.data) && !evt.detail.props.done){
      overallSilentSegments++; //This gets logged at the end of the activity for analytics
      silentSegmentRun++;
      if((silentSegmentRun > CONSECUTIVE_SILENT_THRESHOLD) && !this.state.blankRecording){
        this.stopListening();
        if(mutedRetries == 0){
          this.stopListening();
          logSessionInfo("muted_retry",JSON.stringify(myRecorder.loggingInfo));
          myRecorder.init()
            .then(()=>{
              logSessionInfo("muted_retry_successful",JSON.stringify(myRecorder.loggingInfo));
              this.startListening();
              mutedRetries++;
              silentSegmentRun=0;
            })
            .catch((err)=>{
              mutedRetries++;
              logSentryException(err, "Muted retry failed");
            });
        }else{
          this.setState({blankRecording: true});
          const loggingString = "MUTED RECORDING: " + this.state.activityId + ' ' + JSON.stringify(myRecorder.loggingInfo) + ' ' + overallSilentSegments;
          console.log(loggingString);
          Sentry.captureMessage("MUTED RECORDING");
        }
      }
    }else{
      silentSegmentRun = 0;
      mutedRetries=0;
    }

    //Only execute if there is a buffer to send
    if(evt.detail.data){
      //Check to see if we should send before we set the done flag below (to make sure the first "done" segment gets set)
      let shouldSend = !evt.detail.props.done || this.state.transcriptRequest;

      if((this.props.config.enableAudioRecording || buildAudio) && this.state.activityId){
        let recordingProps = evt.detail.props;
        recordingProps.activityId = this.state.activityId;
        recordingProps.disableBackend = disableBackend;

        if(buildAudio && !evt.detail.allZeros && !this.state.transcriptRequest){
          recordingProps.done = true;
          buildAudio=false;
          myRecorder.stop();
          recordingSession = {};
          if(endMetadata){
            recordingProps=Object.assign(recordingProps,endMetadata);
          }
          console.log("SENDING END OF PHRASE:",Date.now(),recordingProps); //TODO: Remove once confident in error detection
        }

        if(this.state.transcriptRequest){
          if(endMetadata){
            recordingProps=Object.assign(recordingProps,endMetadata);
          }
          recordingProps.done=false;
        }

        if(shouldSend){
          let onFailure = () => {
            this.stopListening();
            this.startListening();
            logSessionInfo("blob_error_recovered",{});
            this.setState({
              speechStreamFailure: true,
            });
          }
          saveSessionAudio(recordingProps.activityId, evt.detail.data,recordingProps,evt.detail.allZeros,onFailure);
          if(recordingProps.comprehensionDone){
            recordingProps.comprehensionDone = false;
            endMetadata=null
          }
        }
      }
    }else{
      nullSegments++;
    }
  }

  getSpeechState(){
    return ({
      speechState: this.state,
      speechProps: this.props,
      recordingSession,
      buildAudio,
      endMetadata,
      silentSegmentRun,
      overallSilentSegments,
      nullSegments,
      totalSegments,
      mutedRetries,
      disableBackend,
    });
  }

  silentSegments(){
    let segmentDuration = myRecorder.sampleRate ? myRecorder.bufferLength/myRecorder.sampleRate : 0;
    let timingOffset = nullSegments * segmentDuration * 1000;

    return {
      silentSegments: overallSilentSegments,
      nullSegments: nullSegments,
      totalSegments: totalSegments,
      sampleRate: myRecorder.sampleRate,
      bufferLength: myRecorder.bufferLength,
      segmentLength: segmentDuration,
      timingOffset: timingOffset,
      disconnects: getDisconnects()
    }
  }

  volumeStatistics(){
    return myRecorder.volume;
  }

  onRecorderStop(evt){
    if(this.state.volumeOnly || (this.state.isListening || buildAudio) && ((Object.keys(recordingSession).length > 0) || this.state.transcriptRequest) && (this.state.activityId)){
      myRecorder.start(recordingSession);
    }
  }

  onRecorderError(evt){
    console.log("RECORDER ERROR: ",evt);
  }

  initializeSpeechSession(activityId,locale="en-us",useStruggleMonitor=false,useSpanishPlacement=false){

    const doInitSpeechSession = () => {
      nullSegments = 0;
      totalSegments = 0;
      overallSilentSegments = 0;
      struggleMonitorEnabled = useStruggleMonitor;
      let forceGoogleNRT = (this.state.altPlacement && locale==="es-mx") && !useSpanishPlacement;
      let useSpanish = useSpanishPlacement && locale ==="es-mx";
      this.setState({activityId,forceGoogleNRT: forceGoogleNRT,useSpanish: useSpanish});
      initSocketCounters();
      logSessionInfo("media_info",myRecorder.loggingInfo);
      let logString =  this.state.altPlacement ? "kaldi":"google";
      if(forceGoogleNRT){
        logString = "googleNRT";
      }else if(useSpanish){
        logString = "googleSpanish"
      }

      logSessionInfo("placement_manager",{placement: logString,spanishNRT:forceGoogleNRT,useSpanish,useStruggleMonitor});

      let loggingString = useStruggleMonitor && getUserData().useStruggleMonitor ? 'struggle_monitor_enabled' : 'struggle_monitor_disabled';

      logSessionInfo(loggingString, {
        customerSetting: getUserData().useStruggleMonitor,
        activityEnable: struggleMonitorEnabled
      });
    }

    // initialize recorder is normally called inside the ready module flow.
    // but in case of that goes away or skipped for whatever reason, recorder is initialized here.
    this.initializeRecorderIfNeeded((error)=>{
      console.error(error)
    }, doInitSpeechSession)
  }

  getPlacementConfig(){
    return({altPlacement: this.state.altPlacement, forceGoogleNRT: this .state.forceGoogleNRT, useSpanish: this.state.useSpanish});
  }

  // set the abortSpeech function passed up from LexaSpeech. This is a temporary solution while Lexa Speech is still a component
  setAbortSpeech(abortSpeech){
    let functionArray = this.state.abortSpeech;
    functionArray.push(abortSpeech);
    this.setState({abortSpeech: functionArray});
  }

  playDance(dance, onDanceEnd){
    dancePending = true;
    this.setState({
      avatarSpeechProps: {
        dance: dance,
        onDanceStart: this.onDanceStart,
        onDanceEnd: onDanceEnd
      }
    });
  }

  destroySound(){
    if (sound) {
      sound.off();
      sound.stop();
      sound.unload();
      sound = null;
    }
  }

  onFinishAppSounds(props){
    // We must destroy the sound object before calling onfinish
    // because the onfinish callback could call playAppSounds again.
    this.destroySound();

    if (props.onfinish) {
      props.onfinish();
    }
  }

  playAppSounds(props) {
    this.destroySound();
    if (iOSSupportsAudioSession()) {
      navigator.audioSession.type = 'playback';
    }
    sound = new Howl({
      src: [props.externalURL || props.url],
      format: ['mp3'],
      volume: props.volume || 0.5,
      html5: shouldForceHTML5Audio(),
      pool: 5,
      onloaderror: (id, error) => {
        console.error('playAppSounds onload error:', error);
        logSentryException(error, "playAppSounds onload error");
        this.onFinishAppSounds(props);
      },
      onplayerror: (id, error) => {
        console.error('playAppSounds onplay error:', error);
        logSentryException(error, "playAppSounds onplay error");
        // This should make sure we play the sound once unlocked and unlock is assume to only happen once.
        if(sound) {
          sound.once('unlock', function() {
            sound.play();
            if(props.onStart){
              props.onStart();
            }
          });
        }
        this.onFinishAppSounds(props);
      },
      onload: () => {
        // Makes sure the sound is still there and not destroyed before playing
        if(sound) {
          sound.play();
        }
      },
      onplay: () => {
        if(props.onStart){
          props.onStart();
        }
      },
      onstop: () => {
        this.onFinishAppSounds(props)
      },
      onend: () => {
        this.onFinishAppSounds(props)
      },
    });
  }

  playAppSoundsWithPromise(props) { // Promise is is used instead of passing "onfinish" param in props
    const onfinishCallback = props.onfinish;
    return new Promise((resolve) => {
      this.playAppSounds({
        ...props,
        onfinish: () => {
          resolve();
          onfinishCallback && onfinishCallback();
        }
      });
    });
  }

  onDanceStart(){
    dancePending = false;
    if(abortDancePending){
      this.abortDance();
    }
  }

  abortDance(){
    if(dancePending){
      abortDancePending = true;
      return;
    }
    if(this.state.abortDance){
      this.state.abortDance();
    }
  }

  setAbortDance(abortDance){
    this.setState({abortDance: abortDance});
  }

  //either speaks the utterance (if the queue is empty), or enques it (if it is not)
  // to be the only exported speak() function
  // returnToListening is an optional param which defines whether or not we should start listening when the speech is finished
  queueSpeech(utterance, returnToListening = false, callback = null, forceQueue = false){
    if(this.state.isSpeaking || speechQueue.length > 0 || forceQueue){
      speechQueue.push({utterance: utterance, callback: callback});
    } else {
      this.setState({isSpeaking: true}, () => this.speak(utterance, returnToListening || this.state.isListening, callback));
    }
  }

  speakWithPromise(utterance, returnToListening) { // Promise is used instead of passing onEnd in utterance object
    return new Promise((resolve) => {
      this.queueSpeech({
        ...utterance,
        onEnd: resolve,
        resolveAbort: resolve,
      }, returnToListening);
    });
  }

  speakSequence(speechProps){
    let speechArray = speechProps.textToSpeak;
    for(let i = 0; i < speechArray.length; i++){
      let speechSegment = speechArray[i];
      let segmentSpeechProps = {
        textToSpeak: speechSegment.textToSpeak,
        passThroughSpeechTags: speechProps.passThroughSpeechTags,
        skipTranslation: speechSegment.skipTranslation,
        onEnd: (i == speechArray.length - 1) && speechProps.onEnd,
      }
      this.queueSpeech(segmentSpeechProps, undefined, undefined, i == 1); //force enqueue the second segment of speech
    }
  }

  /**
   * @deprecated should use the applyAppSounds method directly
   * Replaced the body with applyAppSounds, this is acting as an alias,
   * @param {*} props
   */
  speakAudioFile(props){
    this.playAppSounds({
      url: props.url,
      onfinish: props.onfinish
    });
  }

  //This is how we speak once we have chosen an utterance
  speak(utterance, returnToListening, callback){
    speechPending = true;
    const { locale, bilingualLocale, bilingualMode } = getUserData();
    utterance.locale = utterance.skipTranslation ? locale : (bilingualMode ? bilingualLocale : locale);
    utterance.skipAvatar = this.props.skipAvatar || utterance.skipAvatar; //This is necessary because we are accessing "speak" directly in so many places instead of going through "sendMessage"
    this.stopListening();
    function onStart(){
      speechPending = false;

      if(abortSpeechPending){
        this.abortSpeech(() => {}, abortSpeechPending.props);
        abortSpeechPending = false;
      }

      if(utterance.onStart){
        utterance.onStart();
      }
      this.stopListening();
    }
    function onEnd(){
      if(speechQueue.length > 0){
        let nextUtterance = speechQueue.shift();

        this.speak(nextUtterance.utterance, returnToListening, nextUtterance.callback);
        if(utterance.onEnd){
          utterance.onEnd();
        }
        if(callback){
          callback()
        };
      } else {
        let callbackWithListen = function (returnToListening,callback){
          // if we were listening when we started speaking, start listening again
          if(returnToListening || this.state.pendingStartListening){
            console.log("Resume listening because we were listening")
            const skipCallback = this.state.pendingStartListeningSkipCallback;
            this.setState({pendingStartListening: false, pendingStartListeningSkipCallback: false});
            this.startListening("", null, skipCallback);
          }else{
            console.log("Don't resume listening")
          }
          if(utterance.onEnd){
            utterance.onEnd();
          }
          if(callback){
            callback()
          };
        }
        this.setState({
          isSpeaking: false,
        }, callbackWithListen.bind(this,returnToListening,callback));
      }
    }
    let props = Object.assign({}, utterance);
    props.onStart = onStart.bind(this);
    props.onEnd = onEnd.bind(this, callback);
    this.setState({
      isSpeaking: true,
      lexaSpeechProps: props,
    });
  }

  // takes an optional callback. This is useful if you want to speak immediately after aborting
  // TODO this should also clear the queue
  abortSpeech(callback=null, props={}){
    if(speechPending){
      abortSpeechPending = {callback: callback, props: props};
    }

    this.destroySound();

    let itemsAborted = 0;
    let stateUpdated = false;
    let callbackActivated = false;
    let abortSpeechCallback = () => {
      itemsAborted++;
      if((this.state.abortSpeech && this.state.abortSpeech.length <= itemsAborted) || !this.state.abortSpeech){
        callbackActivated = true;
        if(stateUpdated){
          callback && callback();
        }
      }
    }
    speechQueue = [];
    if(this.state.abortSpeech && this.state.abortSpeech.length > 0){
      for(let i=0; i < this.state.abortSpeech.length; i++){
        this.state.abortSpeech[i](() => {
          abortSpeechCallback();
        });
      }
    } else {
      abortSpeechCallback();
    }

    //TODO: we MUST move this chunk of code. up into `abortSpeechCallback` above, and add `callback`
    //  to the setState callback. We can't do this until we have time for thorough regression testing though,
    //  and adding callbacks to aborts throughout the app. Then we can get rid of `stateUpdated` and `callbackActivated`
    this.setState({
      isSpeaking: false,
    }, () => {
      stateUpdated = true;
      if(callbackActivated){
        abortSpeechCallback();
      }
      if(props.returnToListening){
        this.startListening("abortSpeech returnToListening");
      }
    });
  }

  phonCallback(inPhonemes){
    this.setState({numPhonemes:inPhonemes});
  }

  amirabetCallback(inPhonemes){
    this.setState({abPhonemes: {phon: inPhonemes, time: Date.now()}});
  }

  phonASRcallback(inPhonemes){
    this.setState({phonASRPhonemes: inPhonemes});
  }

  transcriptCallback(transcript){
    this.setState({interimTranscript: transcript, finalTranscript: transcript});
  }

  setPhonemeMode(phonemeMode){
    usePhonASR = phonemeMode;
  }

  getPhonemeMode(phonemeMode){
    return usePhonASR;
  }

  resetPastCombinedPhonemeTranscript() {
    pastCombinedPhonemeTranscript = "";
  }

  togglePhonemeListeningOn(){
    //if we're already listening for phonemes, do nothing
    if(this.getPhonemeMode()){
      return;
    }

    const resetTranscriptFunction = this.useAltPlacement() ? this.resetTranscript.bind(this) : this.props.resetTranscript;

    // set the phoneme mode to true so that the next time we start listening we will use the phoneme asr
    this.setPhonemeMode(true);

    // stop and restart listening in order to make the change to phoneme mode take effect
    this.stopListening("togglePhonemeListeningOn", {
      onFinishCallback: () => {
        // if we have a transcript from listening with the standard placement asr, add it to
        //   our running combined transcript and then reset it
        if(this.props.transcript.final || this.props.transcript.interim){
          pastCombinedPhonemeTranscript += " " + (this.props.transcript.final || this.props.transcript.interim);
          resetTranscriptFunction && resetTranscriptFunction(); //NOTE: this needs to be resetTranscriptFunction() not this.resetTranscriptFunctionToExport() because we don't want to reset pastCombinedPhonemeTranscript here
        }
        this.startListening("togglePhonemeListeningOn");
      }
    });
  }

  togglePhonemeListeningOff(){
    //if we're already using standard placement, do nothing
    if(!this.getPhonemeMode()){
      return;
    }

    // set the phoneme mode to false so that the next time we start listening we will use the standard placement asr
    this.setPhonemeMode(false);

    // stop and restart listening in order to make the change to phoneme mode take effect
    this.stopListening("togglePhonemeListeningOff", {
      onFinishCallback: () => {
        // if we have a transcript from listening with the phoneme asr, add it to
        //   our running combined transcript
        if(this.state.phonASRPhonemes){
          pastCombinedPhonemeTranscript += " " + this.state.phonASRPhonemes;
        }
        this.resetPhonASRPhonemes({callback: this.startListening});
      },
    });
  }

  setPhonemePlacementEnabled(newPhonemePlacementEnabled){
    phonemePlacementEnabled = newPhonemePlacementEnabled;
  }

  getPhonemePlacementEnabled() {
    return phonemePlacementEnabled;
  }

  startListening(source = "", transcriptRequest=null, skipCallback=false, useComprehensionASR=false,comprehensionCallback=null){
    let callback = null;
    if(!this.state.isSpeaking && speechQueue.length <= 0){ //enforce that we can only listen while we are not speaking
      if(this.state.altPlacement){
        if(onSpeechStart){
          onSpeechStart();
        }
        callback = ((transcript)=>{
          if (!skipCallback) {
            this.transcriptCallback(transcript);
          }
        }).bind(this);
      }else{
        this.props.startListening(); // start listening with the webspeech api
      }

      let useStruggleMonitor = getUserData().useStruggleMonitor && struggleMonitorEnabled;
      startPlacementSession(callback, this.phonCallback.bind(this), this.state.altPlacement, this.state.forceGoogleNRT, this.amirabetCallback.bind(this),useStruggleMonitor,this.state.useSpanish,usePhonASR, useComprehensionASR,this.phonASRcallback.bind(this),audioDestination,comprehensionCallback);

      //Defer the recorder start until isListening is set to prevent us from turning off recording as soon as it starts.
      this.setState((prevState) => ({
        isListening: true,
        transcriptRequest: transcriptRequest || prevState.transcriptRequest, //if we are starting listening as part of a transcript request, set the relevant state here. Otherwise, leave transcriptRequest unchanged
      }),()=>{
        if(((Object.keys(recordingSession).length > 0) && (this.state.activityId)) ||
            (this.state.transcriptRequest))
        {
          myRecorder.start(recordingSession);
          resumeSpeechSession();
        }
        this.props.setIsListening(true);
      });
    }
    else{ //Someone tried to start listening while we were speaking. Update the state so we start listening when we're done
      this.setState({
        pendingStartListening: true,
        pendingStartListeningSkipCallback: skipCallback,
      });
    }
  }

  stopListening(source = "", additionalProps={}){
    if (audioDestination) {
      buildAdhocAudio(this.state.activityId, recordingSession);
    }
    this.clearAudioDestination();
    pauseSpeechSession();
    endPlacementSession();
    this.props.stopListening();

    this.props.setIsListening(false);
    this.setState({
      isListening: false,
    },() => {
      myRecorder.stop();
      if(additionalProps.onFinishCallback){
        additionalProps.onFinishCallback();
      }
    });
  }

  requestTranscript(callback,phonemeMode=false,useComprehension=false,comprehensionCallback=null){
    const startTranscriptRequest = () => {
      disableBackend = true;
      usePhonASR = phonemeMode;
      this.startListening("requestTranscript", {
        callback: callback,
      },false,useComprehension,comprehensionCallback);
      this.setState({
        previousTranscript: this.state.transcript.interim,
      });
    }

    // if we are already listening when the transcript request is made, stop listening before
    //   requesting the transcript to make sure that all the listen props get reset appropriately
    if(this.state.isListening){
      this.stopListening("requestTranscript", {onFinishCallback: startTranscriptRequest});
    } else {
      startTranscriptRequest();
    }
  }

  cancelTranscriptRequest(restoreTranscript){
    const {previousTranscript} = this.state;
    usePhonASR = false;
    this.stopListening("requestTranscript");
    this.setState({
      previousTranscript: null,
      transcriptRequest: null,
      phonASRPhonemes: null
    });
    if(restoreTranscript && previousTranscript){
      this.props.resetTranscript(previousTranscript);
    }
    disableBackend = false;
  }

  // starts listening for a match of the transcript to one of the strings in options[]
  // when a match is found with certainty at or above threshold, callback will be called
  // NOTE: there can only be one match request at a time
  requestMatch(options, threshold, callback,startListening=false,checkForMatch=true){
    if(startListening) this.startListening("requestMatch");

    this.setState({
      matchRequest: {
        options: options,
        threshold: threshold,
        callback: callback,
        checkForMatch: checkForMatch,
      }
    });
  }

  cancelMatchRequest(){
    this.setState({
      matchRequest: null,
    });
  }

  // returns the index of the option closest to the current final transcript,
  //   with a certaintanty of cutoff. If no match is found, returns -1
  checkMatch(){
    let transcript = this.state.matchRequest.checkForMatch == true ? this.props.finalTranscript : this.props.transcript;

    if(transcript.length === 0){
      return;
    }

    let matches = stringSimilarity.findBestMatch(transcript, this.state.matchRequest.options);

    if(!this.state.matchRequest.checkForMatch){
      this.props.resetTranscript();
    }

    if(matches.bestMatch.rating >= this.state.matchRequest.threshold){
      this.state.matchRequest.callback(
          this.state.matchRequest.options.indexOf(matches.bestMatch.target)
      );
      this.cancelMatchRequest();
    } else{
      this.state.matchRequest.callback(-1);
    }
  }

  resetTranscript(){
    resetPlacementTranscript();
    this.setState({
      numPhonemes: 0,
      abPhonemes: '',
      transcript: {
        final: null,
        interim: null,
      },
    });
  }

  resetTranscriptFunctionToExport(){
    const resetTranscriptFunction = this.useAltPlacement() ? this.resetTranscript.bind(this) : this.props.resetTranscript;
    this.resetPastCombinedPhonemeTranscript();
    resetTranscriptFunction && resetTranscriptFunction();
  }

  resetPhonASRPhonemes({callback=null}){
    this.setState({
      phonASRPhonemes: null,
    }, callback);
  }

  // Set of methods to allow the application to run the recorder solely for audio measurements
  startVolumeProcessing(){
    this.setState({volumeOnly: true},()=>{myRecorder.start({voumeOnly: true, clearHistory: true})});
  }

  stopVolumeProcessing(){
    this.setState({volumeOnly: false},()=>{myRecorder.stop()});
  }

  setAudioDestination(destination) {
    audioDestination = destination;
  }

  clearAudioDestination() {
    audioDestination = null;
  }

  render() {
    const { isListening, isSpeaking, isQuiet, transcript, avatarSpeechProps, blankRecording, altPlacement, numPhonemes, abPhonemes, speechStreamFailure, inBackground } = this.state;

    let isMicMuted = myRecorder.isMicMuted;

    const propsToPassDown = {
      ...this.state.functions,
      isListening,
      isSpeaking,
      isQuiet,
      avatarSpeechProps,
      isMicMuted,
      blankRecording,
      altPlacement,
      numPhonemes,
      abPhonemes,
      speechStreamFailure,
      inBackground,
    };

    return (
        <div className="speech">
          <LexaSpeech
              config={this.props.config}
              speechProps={this.state.lexaSpeechProps}
              setAbortSpeech={this.setAbortSpeech.bind(this)}
              sendToAvatar={this.setAvatarSpeech}
          />
          {this.props.children(
              propsToPassDown,
              !this.state.transcriptRequest ? transcript : {} // if there's an active transcript request, don't flow the transcript through to the children since it will flow through the callback function instead
          )}
        </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  setIsListening: isListening => dispatch(speechCreators.setIsListening(isListening)),
});

// this is necessary because we need to know whether phoneme placement mode is enabled in NLP.js
//   in order to correctly diff the expected text and the spoken text
export function getPhonemePlacementEnabled(){
  return phonemePlacementEnabled;
}

//Export the component here instead of at top so that we can wrap it in
// the speech recognition component
//
// this adds the following relevant properties to this component:
// transcript: final transcription of all speech that has been spoken into the microphone during the listening period.
// startListening(): Causes the Web Speech API to start listening to speech from the microphone.
// stopListening(): Causes the Web Speech API to stop listening to speech from the microphone, but will finish processing any remaining speech.
//
// source: https://www.npmjs.com/package/react-speech-recognition
export default connect(null, mapDispatchToProps)(SpeechRecognition(Speech));